import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_skusupparm` raw rows into
 * `ap_supplier_products` + `ap_supplier_product_price_breaks` domain rows.
 *
 * Stage 2 of the SKUSUPPARM ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_skusupparm`. For each row in the latest batch:
 *   - Resolves vendor code → `ap_suppliers.id`
 *   - Resolves product SKU → `im_products_v2.id`
 *   - Resolves warehouse code → `im_warehouses.id`
 *   - Upserts into `ap_supplier_products` and `ap_supplier_product_price_breaks`
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_skusupparm rows considered to those touched by the current run.
 */
export const transformSkusupparmSupplierProductsStep = defineStepConfig({
  type: "transform_skusupparm_supplier_products",
  name: "Transform SKUSUPPARM → Supplier Products",
  description:
    "Reads rexel_skusupparm rows updated by the current run, FK-resolves vendor + product + warehouse, and upserts into ap_supplier_products + ap_supplier_product_price_breaks.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    totalRows: z
      .number()
      .describe("Total number of rexel_skusupparm rows processed"),
    upserted: z
      .number()
      .describe("Number of ap_supplier_products rows upserted"),
    priceBreaksUpserted: z
      .number()
      .describe("Number of ap_supplier_product_price_breaks rows upserted"),
    skippedProductCount: z
      .number()
      .describe(
        "Number of distinct product SKUs skipped because they don't exist in im_products_v2",
      ),
    skippedWarehouseCount: z
      .number()
      .describe(
        "Number of distinct warehouse codes skipped (not found in im_warehouses)",
      ),
    skippedSupplierCount: z
      .number()
      .describe(
        "Number of distinct vendor codes skipped (not found in ap_suppliers)",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
